import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { IgxDialogComponent } from '@infragistics/igniteui-angular';
import { UserService } from 'src/app/authentication/user.service';
import { User } from 'src/app/models/user';
import { MessagesService } from 'src/app/services/messages.service';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
    @ViewChild('avatarElement', { read: ElementRef })
    private avatarElement!: ElementRef;

    public avatar!: File | undefined;
    public currentUser: User | undefined;
    public user: FormGroup;
    public file: FormGroup;
    public password: FormGroup;

    constructor(
        public userService: UserService,
        private msgs: MessagesService,
        fb: FormBuilder
    ) {
        this.user = fb.group({
            username: [],
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: ['', Validators.required],
        });
        this.file = fb.group({
            avatar: ['']
        });
        this.password = fb.group({
            password: ['', Validators.required],
            new_password: ['', Validators.required],
            confirm_new_password: ['', Validators.required]
        }, { validators: this.passwordConfirming });
    }

    ngOnInit(): void {
        this.currentUser = this.userService.$user.value;
        this.user.reset(this.currentUser);
        this.userService.$user.subscribe(() => {
            this.currentUser = this.userService.currentUser;
            this.user.reset(this.currentUser);
        });
    }

    public passwordConfirming: ValidatorFn = (c: AbstractControl): ValidationErrors | null => {
        if (c.get('new_password')?.value != c.get('confirm_new_password')?.value) {
            return { invalid: true };
        }
        return null;
    }

    public updateProfile = (dialog: IgxDialogComponent): void => {
        dialog.open();
    }

    public addAvatar = (dialog: IgxDialogComponent): void => {
        if (this.currentUser) {
            dialog.open();
        }
    }

    public updatePassword = (dialog: IgxDialogComponent): void => {
        if (this.currentUser) {
            dialog.open();
        }
    }

    public onProfileSubmit = async (dialog: IgxDialogComponent): Promise<void> => {
        if (this.user.valid) {
            try {
                this.currentUser = await this.userService.update(Object.assign(this.user.value, { id: this.currentUser!.id }) as User);
                this.user.reset(this.currentUser);
                dialog.close();
            }
            catch (err) {
                this.msgs.error(err);
            }
        }
    }

    public onAvatarSubmit = async (dialog: IgxDialogComponent): Promise<void> => {
        if (this.avatar) {
            const formData = new FormData();
            formData.append('file', this.avatar);
            await this.userService.addAvatar(formData);
            this.currentUser = this.userService.currentUser;
            this.file.reset();
            this.avatar = undefined;
            dialog.close();
        }
    }

    public onPasswordSubmit = async (dialog: IgxDialogComponent): Promise<void> => {
        this.userService.updatePassword({ id: this.currentUser?.id, ...this.password.value }).subscribe({
            next: () => {
                this.password.reset();
                dialog.close();
            },
            error: (err) => {
                this.msgs.error(err);
                this.password.reset();
                dialog.close();
            }
        });
    }

    public loadAvatar = (event: Event): void => {
        const files = (event.target as HTMLInputElement).files
        if (files) {
            this.avatar = files[0];
            if (this.avatar) {
                const reader = new FileReader();
                // put onload handler in RAF if this start to not show the picture
                reader.onload = e => this.avatarElement.nativeElement.src = e.target!.result;
                reader.readAsDataURL(this.avatar);
            }
        }
    }

    public cancel = (reason: string, dialog: IgxDialogComponent): void => {
        if (reason === 'avatar') {
            this.avatar = undefined;
            this.file.reset();
        }
        if (reason === 'update') {
            this.user.reset();
        }
        if (reason === 'password') {
            this.password.reset();
        }
        dialog.close();
    }
}
